import type { MessageKey } from "@colorglo/i18n";
import { Button } from "@colorglo/ui/components";
import { iconSize, staffNotificationIcons } from "@colorglo/ui/icons";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useSetStatus } from "../../api/hooks.ts";
import {
  type StaffNotificationView,
  useMarkNotificationsRead,
} from "../../api/staff-notifications.ts";
import { type Translate, useLocale, useT } from "../../lib/locale.ts";
import { formatDayHeading, formatTime, zoned } from "../../lib/time.ts";

/**
 * One entry, as the bell's popover and the notifications page both draw it.
 *
 * Shared rather than duplicated because the two differ only in density: the same sentence,
 * the same two buttons, and above all the same rule about when those buttons exist. A second
 * copy would be the place where "confirm" kept being offered on a booking somebody had
 * already answered.
 */
export function NotificationEntry({
  entry,
  franchiseId,
  timezone,
  showDate = false,
}: {
  readonly entry: StaffNotificationView;
  readonly franchiseId: string;
  readonly timezone: string;
  /** The page groups by day and wants the clock; the bell has no room for either. */
  readonly showDate?: boolean;
}) {
  const t = useT();
  const locale = useLocale();
  const setStatus = useSetStatus(franchiseId);
  const markRead = useMarkNotificationsRead(franchiseId);

  const Glyph = staffNotificationIcons[entry.kind as keyof typeof staffNotificationIcons];
  const awaiting = entry.kind === "booking_requested" && entry.appointment_status === "pending";

  function answer(status: "confirmed" | "cancelled") {
    if (entry.appointment_id === null) return;

    setStatus.mutate(
      {
        appointmentId: entry.appointment_id,
        status,
        ...(status === "cancelled" ? { reason: t("bell.reject_reason") } : {}),
      },
      {
        onSuccess: () => markRead.mutate([entry.id]),
        onError: () => toast.error(t("bell.error")),
      },
    );
  }

  return (
    <li
      className={`flex gap-3 border-s-2 p-3 ${entry.read ? "border-s-transparent" : "border-s-primary"}`}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-muted-foreground">
        {Glyph === undefined ? null : <Glyph {...iconSize.control} />}
      </span>

      <div className="grid min-w-0 flex-1 gap-1">
        <div className="flex items-baseline justify-between gap-3">
          {entry.shop_order_id === null ? (
            <p className="text-body-sm font-medium">{t(`bell.kind.${entry.kind}` as MessageKey)}</p>
          ) : (
            <Link
              to="/butik/bestallningar/$orderId"
              params={{ orderId: entry.shop_order_id }}
              className="text-body-sm font-medium hover:underline"
              onClick={() => {
                if (!entry.read) markRead.mutate([entry.id]);
              }}
            >
              {t(`bell.kind.${entry.kind}` as MessageKey)}
            </Link>
          )}
          {showDate && (
            <time
              dateTime={entry.created_at}
              className="shrink-0 text-caption tabular-nums text-muted-foreground"
            >
              {formatTime(zoned(entry.created_at, timezone))}
            </time>
          )}
        </div>

        <p className="text-caption text-muted-foreground">{bodyOf(t, entry, timezone, locale)}</p>

        {awaiting && (
          <div className="mt-1.5 flex gap-2">
            <Button size="sm" disabled={setStatus.isPending} onClick={() => answer("confirmed")}>
              {t("bell.confirm")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={setStatus.isPending}
              onClick={() => answer("cancelled")}
            >
              {t("bell.reject")}
            </Button>
          </div>
        )}

        {entry.kind === "booking_requested" && !awaiting && (
          <p className="text-caption text-muted-foreground italic">{t("bell.handled")}</p>
        )}
      </div>
    </li>
  );
}

/**
 * The sentence under the heading.
 *
 * Every value in it is read from the booking as it is *now* — the feed row stores foreign
 * keys only — so an erased customer reads as "Kund utan namn" rather than as a name we kept
 * a private copy of.
 */
function bodyOf(
  t: Translate,
  entry: StaffNotificationView,
  timezone: string,
  locale: string,
): string {
  if (entry.kind === "email_failed") return t("bell.body.email_failed");
  if (entry.shop_order_id !== null) {
    return t(`bell.body.${entry.kind}` as MessageKey, { number: String(entry.order_number ?? "") });
  }

  const when =
    entry.starts_at === null
      ? ""
      : (() => {
          const at = zoned(entry.starts_at, timezone);
          return `${formatDayHeading(at.toPlainDate(), locale)} ${formatTime(at)}`;
        })();

  return t(`bell.body.${entry.kind}` as MessageKey, {
    customer: entry.customer_name ?? t("bell.unknown_customer"),
    service: nameOf(entry.service_name_i18n, locale) ?? t("bell.unknown_service"),
    when,
  });
}

function nameOf(names: Record<string, string> | null, locale: string): string | null {
  if (names === null) return null;
  return names[locale] ?? names.sv ?? names.en ?? null;
}
